import { ArrowRight, CircleAlert, Info } from "lucide-react";
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { BadgeLegalStep, isKnownLegalStep } from "@/features/search/components/badge-legal-step";

import type { ClaimWithRelations } from "../api/use-claim";
import { useSendClaimEvent } from "../api/use-claim-mutations";
import type { AdversaryRefusalReason } from "../lib/adversary-refusal-reasons";
import { buildLegalPositionPayload } from "../lib/legal-position";
import { type ClaimMachineSnapshot, previewNextLegalStep } from "../machine/replay-claim-events";
import { AdversaryRefusalReasonCombobox } from "./adversary-refusal-reason-combobox";
import { EventDateTimePicker } from "./event-date-time-picker";
import { EventInsertionPreview } from "./event-insertion-preview";

/** States that should show the adversary refusal reason — mirrors SendEventModal.vue. */
const STATES_WITH_REFUSAL = ["UPCLAIM.WAIT_FOR_AIRLINE_REPLY", "CONCILIATION.WAIT_FOR_AIRLINE_REPLY"];

interface SendEventModalProps {
  open: boolean;
  /** Machine event to confirm (null when the modal is closed). */
  event: string | null;
  claim: ClaimWithRelations;
  snapshot: ClaimMachineSnapshot | null;
  onClose: () => void;
}

/** Port of ui/modals/SendEventModal.vue — confirmation for all machine events. */
export function SendEventModal({ open, event, claim, snapshot, onClose }: SendEventModalProps) {
  const sendClaimEvent = useSendClaimEvent(claim.id);

  const [internalNote, setInternalNote] = useState("");
  // Left undefined on purpose: this component stays mounted for the whole page session, so any
  // `new Date()` seeded here would be the page-load time by the time the admin submits. The API
  // stamps `now()` when nothing is sent — see @/lib/event-date.
  const [eventDate, setEventDate] = useState<Date | undefined>(undefined);
  const [refusalReason, setRefusalReason] = useState<AdversaryRefusalReason | undefined>(undefined);

  const currentLegalStep = claim.claimLegalStep.name;
  const currentClaimState = claim.claimState.type;

  const nextLegalStep = useMemo(() => {
    if (!snapshot || !event) {
      return undefined;
    }
    return previewNextLegalStep({
      litigationName: claim.machine.litigationName,
      versionNumber: claim.machine.versionNumber,
      snapshot,
      event,
    });
  }, [snapshot, event, claim.machine.litigationName, claim.machine.versionNumber]);

  const legalStepWillChange = currentLegalStep !== nextLegalStep;
  const showRefusalReason = STATES_WITH_REFUSAL.includes(currentClaimState);

  function resetForm() {
    setInternalNote("");
    setEventDate(undefined);
    setRefusalReason(undefined);
  }

  function close() {
    resetForm();
    onClose();
  }

  function addEvent() {
    if (!event) {
      return;
    }

    sendClaimEvent.mutate({
      machineEvent: event,
      internalNote: internalNote || undefined,
      eventDate,
      legalPosition: buildLegalPositionPayload({ refusalReason }),
    });
    resetForm();
    onClose();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          close();
        }
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            {legalStepWillChange ? (
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
                <CircleAlert className="h-6 w-6 text-amber-600" aria-hidden="true" />
              </span>
            ) : (
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100">
                <Info className="h-6 w-6 text-blue-600" aria-hidden="true" />
              </span>
            )}
            <DialogTitle>Envoyer un évènement</DialogTitle>
          </div>
          <DialogDescription>
            Vous êtes sur le point d'ajouter un évènement à la claim.
            <br />
            Êtes-vous sûr de continuer ?
          </DialogDescription>
        </DialogHeader>

        <DialogPanel className="space-y-4">
          <div
            className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-800"
            role="alert"
          >
            <div className="mb-3">
              <p className="mb-3 font-medium">Évènement:</p>
              <span className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-900">
                {event}
              </span>
            </div>

            {nextLegalStep && legalStepWillChange ? (
              <div className="mt-8">
                <span className="font-bold">L'étape juridique du dossier va changer:</span>
                <div className="my-3 flex items-center">
                  <BadgeLegalStep name={currentLegalStep} />
                  <ArrowRight className="mx-2 h-5 w-5 text-gray-500" />
                  {isKnownLegalStep(nextLegalStep) ? (
                    <span className="mr-4">
                      <BadgeLegalStep name={nextLegalStep} />
                    </span>
                  ) : (
                    <span className="mr-4 text-xs font-medium">{nextLegalStep}</span>
                  )}
                </div>
              </div>
            ) : null}
          </div>

          <div>
            <div className="my-2">
              <Label htmlFor="eventDate" className="block text-sm font-medium text-gray-700">
                Date de l'évènement
              </Label>
              <div className="mt-1">
                <EventDateTimePicker id="eventDate" value={eventDate} onChange={setEventDate} />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Laisser sur « Aujourd'hui » pour dater l'évènement à maintenant, ou choisir la date et l'heure
                du dernier message reçu.
              </p>
              {event ? (
                <EventInsertionPreview
                  events={claim.claimEvents ?? []}
                  eventDate={eventDate}
                  machineEvent={event}
                />
              ) : null}
            </div>

            <div className="space-y-6">
              {showRefusalReason ? (
                <AdversaryRefusalReasonCombobox value={refusalReason} onChange={setRefusalReason} />
              ) : null}
            </div>

            <div className="mt-6">
              <Label htmlFor="comment" className="block text-sm font-medium text-gray-700">
                Ajouter une note
              </Label>
              <Textarea
                id="comment"
                rows={4}
                value={internalNote}
                onChange={(e) => setInternalNote(e.target.value)}
                placeholder="Exemple : Un commentaire sur la réponse de la compagnie"
                className="mt-1"
              />
            </div>
          </div>
        </DialogPanel>

        <DialogFooter>
          <Button
            variant={legalStepWillChange ? "destructive" : "default"}
            onClick={addEvent}
            disabled={sendClaimEvent.isPending}
          >
            Envoyer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
